import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/organisms/auth/ProtectedRoute';

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
      <h2 className="text-xl font-semibold text-gray-900">Loading...</h2>
    </div>
  </div>
);

// Lazy load pages - Public pages are loaded immediately for better first impression
import { LoginPage } from '@/components/pages/LoginPage';
import { ForgotPasswordPage } from '@/components/pages/ForgotPasswordPage';
import { UnauthorizedPage } from '@/components/pages/UnauthorizedPage';

// Lazy load protected pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement').then(m => ({ default: m.UserManagement })));
const GroupManagement = lazy(() => import('@/pages/admin/GroupManagement').then(m => ({ default: m.GroupManagement })));
const ChatMonitor = lazy(() => import('@/pages/admin/ChatMonitor').then(m => ({ default: m.ChatMonitor })));
const Settings = lazy(() => import('@/pages/admin/Settings').then(m => ({ default: m.Settings })));
const GroupModels = lazy(() => import('@/pages/admin/GroupModels').then(m => ({ default: m.GroupModels })));
const UsageMonitor = lazy(() => import('@/pages/admin/UsageMonitor').then(m => ({ default: m.UsageMonitor })));

// Teacher Pages
const TeacherDashboard = lazy(() => import('@/pages/teacher/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })));
const GroupMonitoringView = lazy(() => import('@/pages/teacher/GroupMonitoringView').then(m => ({ default: m.GroupMonitoringView })));

// Student Pages
const StudentDashboard = lazy(() => import('@/pages/student/StudentDashboard').then(m => ({ default: m.StudentDashboard })));
const ChatInterface = lazy(() => import('@/pages/student/ChatInterface').then(m => ({ default: m.ChatInterface })));

// Role-based Dashboard
const RoleDashboard = lazy(() => import('@/components/pages/RoleDashboard').then(m => ({ default: m.RoleDashboard })));

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider
          onSessionExpiry={() => {
            // Session expired - could show a notification here
          }}
          onSessionWarning={() => {
            // Session expiring soon - could show a warning notification here
          }}
        >
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Protected routes - All authenticated users */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Suspense fallback={<LoadingFallback />}><RoleDashboard /></Suspense>} />
              <Route path="/chat" element={<Suspense fallback={<LoadingFallback />}><ChatInterface /></Suspense>} />
              <Route path="/chat/:groupId" element={<Suspense fallback={<LoadingFallback />}><ChatInterface /></Suspense>} />
            </Route>

            {/* Teacher-only routes */}
            <Route element={<ProtectedRoute requiredRole="teacher" />}>
              <Route path="/teacher/dashboard" element={<Suspense fallback={<LoadingFallback />}><TeacherDashboard /></Suspense>} />
              <Route path="/teacher/monitor" element={<Suspense fallback={<LoadingFallback />}><ChatMonitor /></Suspense>} />
              <Route path="/groups/:groupId" element={<Suspense fallback={<LoadingFallback />}><GroupMonitoringView /></Suspense>} />
            </Route>

            {/* Admin-only routes */}
            <Route element={<ProtectedRoute requiredRole="admin" />}>
              <Route path="/admin/dashboard" element={<Suspense fallback={<LoadingFallback />}><AdminDashboard /></Suspense>} />
              <Route path="/admin/users" element={<Suspense fallback={<LoadingFallback />}><UserManagement /></Suspense>} />
              <Route path="/admin/groups" element={<Suspense fallback={<LoadingFallback />}><GroupManagement /></Suspense>} />
              <Route path="/admin/groups/:groupId/models" element={<Suspense fallback={<LoadingFallback />}><GroupModels /></Suspense>} />
              <Route path="/admin/monitor" element={<Suspense fallback={<LoadingFallback />}><ChatMonitor /></Suspense>} />
              <Route path="/admin/usage" element={<Suspense fallback={<LoadingFallback />}><UsageMonitor /></Suspense>} />
              <Route path="/settings" element={<Suspense fallback={<LoadingFallback />}><Settings /></Suspense>} />
            </Route>

            {/* Student-only routes */}
            <Route element={<ProtectedRoute requiredRole="student" />}>
              <Route path="/student/dashboard" element={<Suspense fallback={<LoadingFallback />}><StudentDashboard /></Suspense>} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
